/*
Government Purpose Rights (“GPR”)
Contract No.  W911NF-14-D-0005
Contractor Name:   University of Southern California
Contractor Address:  3720 S. Flower Street, 3rd Floor, Los Angeles, CA 90089-0001
Expiration Date:  Restrictions do not expire, GPR is perpetual
Restrictions Notice/Marking: The Government's rights to use, modify, reproduce, release, perform, display, or disclose this software are restricted by paragraph (b)(2) of the Rights in Noncommercial Computer Software and Noncommercial Computer Software Documentation clause contained in the above identified contract.  No restrictions apply after the expiration date shown above. Any reproduction of the software or portions thereof marked with this legend must also reproduce the markings. (see: DFARS 252.227-7014(f)(2)) 
No Commercial Use: This software shall be used for government purposes only and shall not, without the express written permission of the party whose name appears in the restrictive legend, be used, modified, reproduced, released, performed, or displayed for any commercial purpose or disclosed to a person other than subcontractors, suppliers, or prospective subcontractors or suppliers, who require the software to submit offers for, or perform, government contracts.  Prior to disclosing the software, the Contractor shall require the persons to whom disclosure will be made to complete and sign the non-disclosure agreement at 227.7103-7.  (see DFARS 252.227-7025(b)(2))
*/
import * as React from "react";
import { Position, useTour } from "@reactour/tour";
import {
  ActivityType,
  Tooltip,
  Verb,
} from "@ictlearningsciences/pal3-core/types";
import { TOOLTIPS } from "@ictlearningsciences/pal3-core/dialogue";

import { LoadStatus } from "../helpers";
import { useWithDialogue } from "../hooks/useWithDialogue";
import { useWithUserStudy } from "../hooks/useWithUserStudy";
import { setTooltip as _setTooltip } from "../store/slices/drawer";
import { useAppDispatch, useAppSelector } from "../store/hooks";

export function TooltipOverlay(props: {
  id: string;
  children: JSX.Element | JSX.Element[];
  position?: Position;
}): JSX.Element {
  const dispatch = useAppDispatch();
  const { records, recordLoadState, recordsProcessed } = useAppSelector(
    (state) => state.userData
  );
  const { dialogueMessages } = useAppSelector((state) => state.data);
  const { viewedTooltips } = useAppSelector((state) => state.firstTimeData);
  const [tooltip, setTooltip] = React.useState<Tooltip>();
  const [init, setInit] = React.useState<boolean>(false);
  const { setIsOpen } = useTour();
  const { playMessage } = useWithDialogue();
  const { inStudy } = useWithUserStudy();

  React.useEffect(() => {
    if (!inStudy("joinISRL")) {
      return;
    }
    const timeout = setTimeout(() => setInit(true), 500);
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, []);

  React.useEffect(() => {
    if (!inStudy("joinISRL")) {
      return;
    }
    const tooltip: Tooltip = TOOLTIPS.find((t) => t.alias === props.id);
    if (
      !init ||
      !tooltip ||
      !recordsProcessed ||
      recordLoadState.status !== LoadStatus.DONE
    )
      return;
    const tooltipSeen =
      viewedTooltips.includes(props.id) ||
      records.find(
        (r) =>
          r.verb === Verb.COMPLETED &&
          r.objType === ActivityType.INTERACTION &&
          r.objId === props.id
      );
    if (!tooltipSeen) {
      for (const prereq of tooltip.prerequisites || []) {
        const prereqRecord = records.find(
          (r) =>
            r.verb === Verb.COMPLETED &&
            r.objType === ActivityType.INTERACTION &&
            r.objId === prereq
        );
        if (!prereqRecord) return;
      }
      setIsOpen(true);
      setTooltip(tooltip);
      dispatch(_setTooltip(tooltip));
      if (tooltip?.message) {
        const msg = dialogueMessages.find(
          (m) => m.alias === tooltip.message.alias
        );
        playMessage(msg || tooltip.message);
      }
    }
  }, [init, records, recordsProcessed]);

  return <div className={tooltip?.alias}>{props.children}</div>;
}
